<template>
  <div class="container detail-shell px-4">
    <Spinner v-if="loadingStatus" text="Retrieving data..." classes="text-dark-8 mh-60" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />
    <main v-else-if="origins && origins.length === 0">
      <header class="flex items-center mb-3 mt-5">
        <h1 ref="title" class="page-title m-0">
          Your origin
        </h1>
      </header>
      <section class="text-center mt-200">
        <h2 class="text-s-m text-dark-9 text-500 mb-2">Bring Your Own Origin</h2>
        <p class="text-s-s text-dark-8 text-normal max-w-md mx-auto mb-2">Have your own media server? Use our
          high-performance CDN <br>to reduce load on your origin and ensure low-latency global delivery.</p>
        <a class="text-dark-8 text-s-ss text-500 mb-4 flex items-center justify-center"
          href="https://docs.castr.com/en/articles/8392177-create-custom-cdn" target="_blank">
          <icon-base class="mr-1 text-[#ADB8DC] opacity-50"><icon-book-open /></icon-base>
          How Your Origin work
        </a>
        <FormButton v-if="maxOriginsSub > 0 && !isLiveSubscriptionExpired" size="md" type="main"
          @click="$showModal('modal-add-origin')">
          <icon-base class="mr-2" :width="12" :height="12" :width-view-box="12" :height-view-box="12"><icon-plus-small /></icon-base>
          Create a new CDN resource
        </FormButton>
        <a v-else href="/app/subscribe">
          <FormButton size="md" type="main">
            Upgrade
          </FormButton>
        </a>
      </section>
    </main>
    <main v-else>
      <header class="flex items-center mb-3 mt-5">
        <h1 ref="title" class="page-title m-0">
          Your origin
        </h1>
        <span id="exceed-origin-limit-tooltip" class="ml-auto">
          <FormButton :disabled="maxOriginsSub === 0 || origins.length >= maxOriginsSub || isLiveSubscriptionExpired"
            size="md" type="main" @click="$showModal('modal-add-origin')" class="ml-auto">
            <icon-base class="mr-2 flex-shrink-0" :width="12" :height="12" :width-view-box="12" :height-view-box="12"><icon-plus-small /></icon-base>
            Create a new CDN resource
          </FormButton>
        </span>

        <Tooltip><span class="inline-block"></span><template #content>= maxOriginsSub || isLiveSubscriptionExpired"
          target="exceed-origin-limit-tooltip" placement="top">
          To create more CDN resources, contact Sales team.
        </template></Tooltip>
      </header>

      <Alert v-if="isLiveSubscriptionExpired && bandwidthLimitReachedMsg" type="error"
        class="flex items-start px-2">
        <div class="-mt-1">
          <p class="text-s-m text-500 mb-0">You've reached your bandwidth limit.</p>
          <p class="text-s-s text-500 mb-0">The origin will be disabled. Please contact <a
              class="text-reset text-underline" href="#" onclick="Intercom('show')">sales</a> team to get more bandwidth.
          </p>
        </div>
      </Alert>

      <Alert v-if="isLiveSubscriptionExpired && !bandwidthLimitReachedMsg" type="error"
        class="flex items-start px-2">
        <div class="-mt-1">
          <p class="text-s-m text-500 mb-0">Your subscription has expired.</p>
          <p class="text-s-s text-500 mb-0">The origin will be disabled. Please <a class="text-reset text-underline"
              href="/app/subscribe?category=live">upgrade plan.</a> </p>
        </div>
      </Alert>

      <section>
        <ul v-for="(orig, index) in origins" :key="index" class="list-none">
          <div class="bg-dark-2 origin-item-container" style="padding: 20px;">

            <div class="flex justify-between items-center">
              <!-- <h2 class="text-s-xxl text-500 mb-1">{{ orig.name }}</h2> -->

              <textarea v-model="orig.name" style="width: 90%;" ref="streamTitle" rows="1" autocomplete="off"
                autocorrect="off" autocapitalize="off" spellcheck="false" title="Edit stream name"
                class="page-title page-title_input -ml-2 pl-2 overflow-hidden form-control form-control-sm"
                @keydown.enter.prevent="preventEnter" @change="onOriginNameChange(index)" @focus="resizeTextarea"
                @keyup="resizeTextarea"></textarea>

              <div class="flex">
                <div v-if="orig.originUrl">
                  <span id="exceed-origin-limit-tooltip-toggle">
                    <FormButton v-if="orig.enabled" :disabled="isLiveSubscriptionExpired" @click="toggleOrigin(index)"
                      type="secondary" size="sm" class="mr-2">
                      Disable
                    </FormButton>
                    <FormButton v-else :disabled="isLiveSubscriptionExpired" @click="toggleOrigin(index)" type="main"
                      size="sm" class="mr-2">
                      Enable
                    </FormButton>
                  </span>

                  <Tooltip><span class="inline-block"></span><template #content>
                    <p class="text-s-s text-500 mb-0">Origin disabled. Please <a class="text-reset text-underline"
                        href="/app/subscribe?category=live">upgrade plan.</a> </p>
                  </template></Tooltip>

                </div>
                <FormButton @click="onRemoveOrigin(index)" type="danger" size="sm" class="px-0">
                  <icon-base :width="16" :height="16" width-view-box="20" height-view-box="21" fill="none"><icon-bucket /></icon-base>
                </FormButton>
              </div>
            </div>

            <div v-if="!orig.originUrl">
              <div class="flex md:justify-end items-center">
                <FormInput label="Your origin Domain" v-model="orig.originUrlInput" class="mr-2" size="md"
                  placeholder="Enter the domain or IP address" />
                <FormButton class=" truncate" :disabled="!orig.originUrlInput || orig.originProcessing"
                  @click="addOriginUrl(index)" type="main">{{ orig.originProcessing ? 'Saving…' : 'Add CDN'}}</FormButton>
              </div>

              <div class="mt-3">
                <a href="#" @click.prevent id="example-url-tooltip" class="text-s-s text-dark-7">
                  Example URL
                </a>
                <Tooltip><span class="inline-block"></span><template #content>
                  <div class="tooltip-content">
                    <p class="text-dark-8 test-s-s">To stream your video via the CDN, you simple need to replace the URLs
                      pointing to your static files with the <span style="color: #fff">New url</span> host name. Your
                      video date will ve automatically downloaded, cached and delivered through the CDN.</p>

                    <p class="text-dark-8 test-s-s">For example, update the video file URLs pointing to:</p>
                    <FormInput model-value="http://myvideoserver.com/videos/playlist.m3u8" readonly
                      custom-classes="!bg-[rgba(167,181,226,0.08)] !border-none" />

                    <br>
                    <p class="text-dark-8 test-s-s">to your <span style="color: #fff">New url</span> hostname:</p>
                    <FormInput model-value="http://castr.b-cdn.net/video/playlist.m3u8" readonly
                      custom-classes="!bg-[rgba(167,181,226,0.08)] !border-none" />

                  </div>
                </template></Tooltip>
              </div>

            </div>

            <TabGroup v-else type="lines" customClasses="tabs-light mt-2">
              <TabItem
                type="line"
                label="Origin configuration"
                :isActive="true"
              />
              <TabItem type="line" disabled>
                Real-time analytics
                <Badge type="main" class="badge_beta self-center mr-2">Coming soon</Badge>
              </TabItem>
            </TabGroup>
            <div class="mt-3">
                <div class="mt-3 flex flex-wrap items-center">
                  <div class="mt-3 w-1/4">
                    <div class="flex items-center flex-nowrap">
                      <FormInput label="Origin URL" :modelValue="orig.originUrl" readonly class="flex-1" customClasses="!bg-transparent !border-0" />
                        <FormButton class="py-1 px-2 m-0" type="light" :isIcon="true" @click="clipboardCopy(orig.originUrl)">
                          <icon-base :width="20" :height="20" :width-view-box="20" :height-view-box="20" fill="none"><icon-copy-outline /></icon-base>
                        </FormButton>
                    </div>

                  </div>
                  <div class="mt-3 w-1/4">
                    <div class="flex items-center flex-nowrap">
                      <FormInput label="CDN URL" :modelValue="'https://' + orig.cdnUrl" readonly class="flex-1" customClasses="!bg-transparent !border-0" />
                        <FormButton class="py-1 px-2 m-0" type="light" :isIcon="true" @click="clipboardCopy('https://' + orig.cdnUrl)">
                          <icon-base :width="20" :height="20" :width-view-box="20" :height-view-box="20" fill="none"><icon-copy-outline /></icon-base>
                        </FormButton>
                    </div>
                  </div>
                  <div class="w-1/12">
                    <FormButton @click="onEditOrigin(index)" type="secondary" size="sm" class="mr-2"
                      style="margin-top: 40px;">
                      Edit
                    </FormButton>
                  </div>

                  <div class="mx-5" style="border-left:1px solid #343E61; height: 80px;"></div>

                  <div class="w-full">
                    <label class="text-s-s text-dark-7 text-500">Bandwidth</label><br>
                    <span v-if="orig.bandwidthUsage" class="text-s-s color-text-1">{{ orig.bandwidthUsage.value }} {{
                      orig.bandwidthUsage.unit.toUpperCase() || 'GB' }} </span>
                    <span v-else class="fas fa-spinner fa-spin mr-4"></span>

                    <span class="text-s-s text-dark-7"> used by CDN</span><br>

                    <span v-if="totalBandwidthUsage" class="text-s-s color-text-1"
                      :class="{ 'text-danger': bandwidthLimitReachedMsg }">{{ totalBandwidthUsage.value || 0 }} {{
                        totalBandwidthUsage.unit.toUpperCase() || 'GB' }} / </span>
                    <span v-else class="fas fa-spinner fa-spin mr-4"></span>

                    <span class="text-s-s text-dark-7">{{ totalBandwidthLimit.value }} {{ totalBandwidthLimit.mappedUnit
                    }} assigned</span>
                  </div>
                </div>
                <div class="mt-4 flex flex-wrap items-center">
                  <div class="example-links-container">
                    <label class="text-s-s text-dark-7 text-500">Example URL</label>
                    <p class="text-s-s color-text-1">https://{{ orig.originUrl }}/content/media.mp4</p>

                    <label class="text-s-s text-dark-7 text-500">CDN Content URL is</label>
                    <p class="text-s-s color-text-1">https://{{ orig.cdnUrl }}/content/media.mp4</p>

                  </div>
                </div>
            </div>

          </div>
        </ul>
      </section>
    </main>
    <add-origin-modal @origin-created="originCreated" />
    <edit-origin-modal :edit-origin="editOriginUrl" />
    <confirm-modal modalId="delete-cdn" message="Are you sure you want to delete the CDN resource?" modal-type="danger"
      @modal-confirm="removeOrigin()" />
  </div>
</template>

<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import _ from "lodash";
import { mapGetters } from 'vuex'
import Spinner from "@/components/ui/Spinner.vue"
import OriginService from "@/services/OriginService"
import AddOriginModal from "@/components/modals/AddOriginModal.vue"
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
// TODO MIGRATION
// import VueCircle from 'vue2-circle-progress'
import { bytes } from '@/filtersNew'
import EditOriginModal from "@/components/modals/EditOriginModal.vue"
import UserService from '@/services/UserService'
import MetricsService from '@/services/MetricsService'
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import Badge from '@/components/Badge.vue';
import Alert from '@/components/Atoms/Alert.vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconBookOpen from '@/components/icon/IconBookOpen.vue';
import IconPlusSmall from '@/components/icon/IconPlusSmall.vue';
import IconBucket from '@/components/icon/IconBucket.vue';
import IconCopyOutline from '@/components/icon/IconCopyOutline.vue';

export default {
  name: "ManagePlaylists",
  components: {
		Tooltip,
    FormButton,
    FormInput,
    Spinner,
    AddOriginModal,
    ConfirmModal,
    // TODO MIGRATION
    // FIXME extra import
    // VueCircle,
    EditOriginModal,
    Alert,
    Badge,
    TabGroup,
    TabItem,
    IconBase,
    IconBookOpen,
    IconPlusSmall,
    IconBucket,
    IconCopyOutline,
	},
  data() {
    return {
      origins: [],
      originProcessing: false,
      currentIndexed: 0,
      progress: 0,
      totalOriginBandwidth: null,
      totalBandwidthUsage: null,
      bandwidthLimitReachedMsg: false
    };
  },
  computed: {
    ...mapGetters({
      loadingStatus: "Ui/loadingStatus",
      isLiveSubscriptionExpired: "User/isLiveSubscriptionExpired",
      hasLiveSubscription: "User/hasLiveSubscription"
    }),
    liveSub() {
      return this.$store.state.User.subscriptions.find(sub => ((sub.category && sub.category.includes('live'))))
    },
    maxOriginsSub() {
      return _.get(this.liveSub, ['package', 'definition', 'maxOrigins'], 0)
    },
    originBandwidth() {
      let bandwidth = 0
      if (this.totalOriginBandwidth) {
        bandwidth = this.totalOriginBandwidth
      }
      const data = bytes(bandwidth, true, 3, true)
      return { value: data.value, unit: data.unit }
    },
    totalBandwidthLimit() {
      let mappedUnit = 'TB'
      let allocatedVal = this.getSubscriptionDefProp(this.liveSub, 'bandwidth')

      if (allocatedVal < 1) {
        mappedUnit = 'MB'
      } else if (allocatedVal < 1000) {
        mappedUnit = 'GB'
      }

      let val = allocatedVal
      if (allocatedVal < 1) {
        val *= 1000
      } else if (allocatedVal >= 1000) {
        val = (allocatedVal / 1000).toFixed(3)
      }
      return { value: val, mappedUnit }
    }
  },
  async mounted() {
    this.$store.dispatch('Ui/setLoadingStatus', true)
    try {
      const data = await OriginService.getOrigins()
      this.origins = _.map(data, (item, idx) => {
        item.originProcessing = false
        item.originUrlInput = null
        item.bandwidthUsage = null
        return item
      }).reverse()

    } catch (err) {
      console.error(err)
    }

    setTimeout(async () => {
      const originBandwidth = await OriginService.getUserOriginBandwidth()
      this.totalOriginBandwidth = originBandwidth.bytes
      await this.getTotalBandwidthUsage()
      await this.loadOriginBandwidth()
    }, 0)

    this.$store.dispatch('Ui/setLoadingStatus', false)
  },
  methods: {
    async getTotalBandwidthUsage() {
      const addonSub = this.liveSub
      const bwLimit = this.getSubscriptionDefProp(addonSub, 'bandwidth')
      if (bwLimit) {
        let bwBytes = 0
        try {
          let res = await MetricsService.getSubscriptionBandwidth(UserService.getUserId(this.activeTeam), addonSub.package._id)
          bwBytes = res ? res.bytes : 0
          bwBytes = bwBytes >= 100000000 ? bwBytes : 0; // only show bandwidth > 100MB
        } catch (e) { console.log('e', e) }

        this.totalBandwidthUsage = bytes(bwBytes, true, 3, true)

        let { value, mappedUnit } = this.totalBandwidthLimit
        if (mappedUnit === 'TB') {
          value = value * 1000 ** 4
        }
        if (mappedUnit === 'GB') {
          value = value * 1000 ** 3
        }
        if (mappedUnit === 'MB') {
          value = value * 1000 * 1000
        }

        if (bwBytes > value) {
          this.bandwidthLimitReachedMsg = true
        }
      }
    },
    getSubscriptionDefProp(sub, prop) {
      return _.get(sub, 'definitionOverride.' + prop) || _.get(sub, 'package.definition.' + prop)
    },
    onEditOrigin(index) {
      this.currentIndexed = index
      showModal('modal-edit-origin');
    },
    async editOriginUrl(url) {
      const originFiltered = url.replace('https://', '').replace('http://', '')
      const index = this.currentIndexed
      try {
        await OriginService.updateOrigin(this.origins[index]._id, { originUrl: originFiltered })
        this.origins[index].originUrl = originFiltered
        this.currentIndexed = null
        this.$notify({ group: 'success', text: 'CDN URL updated.' })
      } catch (err) {
        console.error(err)
        this.$notify({
          group: 'error',
          text: err.message || 'Edit CDN failed'
        });
      }
    },
    resizeTextarea: function (e) {
      let area = e.target;
      area.style.height = area.scrollHeight + 'px';
    },
    preventEnter(e) {
      e.preventDefault();
    },
    originCreated(origin) {
      this.origins.unshift(Object.assign(origin, { originUrlInput: null, originUrl: null, originProcessing: null }))
    },
    async onOriginNameChange(index) {
      if (this.origins[index].name === '') {
        return
      }

      try {
        await OriginService.updateOrigin(this.origins[index]._id, { name: this.origins[index].name })
        this.$notify({ group: 'success', text: 'CDN name changed' })
      } catch (err) {
        console.error(err)
        this.$notify({
          group: 'error',
          text: err.message || 'Change CDN name failed'
        });
      }
    },
    async loadOriginBandwidth() {
      _.map(this.origins, async (item, index) => {
        try {
          const data = await OriginService.getOriginServiceBandwidth(item._id)
          this.origins[index].bandwidthUsage = bytes(data.bytes, true, 3, true)
        } catch (err) {
          console.error(err)
        }
      })
    },
    async addOriginUrl(index) {
      this.origins[index].originProcessing = true
      const originFiltered = this.origins[index].originUrlInput.replace('https://', '').replace('http://', '')
      try {
        const origin = await OriginService.updateOrigin(this.origins[index]._id, { originUrl: originFiltered })
        this.origins[index] = Object.assign(origin, { originProcessing: null, originUrl: originFiltered })
        this.$notify({ group: 'success', text: 'CDN added' })
      } catch (err) {
        console.error(err)
        this.$notify({
          group: 'error',
          text: err.message || 'Add CDN failed'
        });
      }

      this.origins[index].originProcessing = false
    },
    async onRemoveOrigin(index = null) {
      this.currentIndexed = index
      showModal('delete-cdn');
    },
    async removeOrigin() {
      try {
        await OriginService.deleteOrigin(this.origins[this.currentIndexed]._id)
        this.origins.splice(this.currentIndexed, 1);
        this.$notify({ group: 'success', text: 'CDN resource removed' })
      } catch (err) {
        console.error(err)
        this.$notify({
          group: 'error',
          text: err.message || 'remove CDN failed'
        });
      }
    },
    async toggleOrigin(index) {
      try {
        await OriginService.toggleOrigin(this.origins[index]._id)
        this.origins[index].enabled = !this.origins[index].enabled
      } catch (err) {
        console.error(err)
        this.$notify({
          group: 'error',
          text: err.message || 'toggle CDN failed'
        });
      }
    },
    async toggleFeature(featureName) {
      if (featureName === 'responsive' || featureName === 'audioOnly' || featureName === 'loop' || featureName === 'muted' || featureName === 'hiddenControl' || featureName === 'hiddenVolume') {
        this.featureProcessing[featureName] = !this.featureProcessing[featureName]
      }
    },
    clipboardCopy(text) {
      try {
        if (text instanceof Function) text = text();

        this.$copyText(text);
        this.$notify({ group: 'info', text: 'Copied to clipboard' });
      } catch (e) { }
    },
  },
};
</script>

<style scoped>
.origin-item-container {
  border-radius: 6px;
}

.tooltip-container {
  max-width: 750px !important;
}

:deep(.tooltip-container .tooltip-inner) {
  max-width: none;
}

.tooltip-content {
  text-align: left;
  padding: 5px;
}

.tooltip-input {
  color: red;
}

.page-title {
  font-size: 23px !important;
}

.example-links-container {
  background-color: var(--c-dark-3);
  padding: 12px 16px 12px 16px;
  border-radius: 8px;
  margin-left: 15px;
}</style>
<style>.circle-percent-text-body {
  width: 105% !important;
  height: 90% !important;
}

.circle-percent-text-body>.percent-text {
  font-size: 16px !important;
  font-weight: 500 !important;
}</style>
